import { memo, useState } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { UserCheck, Settings } from 'lucide-react';
import { Card, Input, Label, Button } from '@caruso/ui';

export interface CheckClientNodeData {
  label?: string;
  phoneSource?: 'context' | 'fixed';
  phoneField?: string; // Campo do contexto (ex: "phone")
  fixedPhone?: string; // Telefone fixo
  createIfNotExists?: boolean;
  clientName?: string; // Nome para criar o cliente se não existir
}

function CheckClientNode({ data, selected }: NodeProps<CheckClientNodeData>) {
  const [showConfig, setShowConfig] = useState(false);

  return (
    <div className="relative">
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-brand-gold border-2 border-brand-ink"
      />

      <Card
        className={`min-w-[280px] bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-2 transition-all ${
          selected ? 'border-brand-gold shadow-lg shadow-brand-gold/20' : 'border-blue-500/30'
        }`}
      >
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <UserCheck className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-brand-ivory">
                {data.label || 'Verificar Cliente'}
              </h3>
              <p className="text-xs text-brand-ivory/60">
                Valida se o cliente existe
              </p>
            </div>
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="p-1.5 rounded hover:bg-brand-gold/10 transition-colors"
            >
              <Settings className="w-4 h-4 text-brand-ivory/60" />
            </button>
          </div>

          {showConfig && (
            <div className="space-y-3 pt-3 border-t border-blue-500/20">
              <div>
                <Label className="text-brand-ivory text-xs mb-1">Origem do Telefone</Label>
                <select
                  value={data.phoneSource || 'context'}
                  onChange={(e) => {
                    if (data.onChange) {
                      data.onChange({
                        ...data,
                        phoneSource: e.target.value as 'context' | 'fixed',
                      });
                    }
                  }}
                  className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded text-brand-ivory text-sm"
                >
                  <option value="context">Do Contexto</option>
                  <option value="fixed">Fixo</option>
                </select>
              </div>

              {data.phoneSource === 'context' ? (
                <div>
                  <Label className="text-brand-ivory text-xs mb-1">
                    Campo do Contexto
                  </Label>
                  <Input
                    value={data.phoneField || 'phone'}
                    onChange={(e) => {
                      if (data.onChange) {
                        data.onChange({
                          ...data,
                          phoneField: e.target.value,
                        });
                      }
                    }}
                    placeholder="phone"
                    className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory text-sm"
                  />
                  <p className="text-xs text-brand-ivory/40 mt-1">
                    Nome do campo no contexto (ex: phone, userPhone)
                  </p>
                </div>
              ) : (
                <div>
                  <Label className="text-brand-ivory text-xs mb-1">
                    Telefone Fixo
                  </Label>
                  <Input
                    value={data.fixedPhone || ''}
                    onChange={(e) => {
                      if (data.onChange) {
                        data.onChange({
                          ...data,
                          fixedPhone: e.target.value,
                        });
                      }
                    }}
                    placeholder="5511999999999"
                    className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory text-sm"
                  />
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="createIfNotExists"
                  checked={data.createIfNotExists || false}
                  onChange={(e) => {
                    if (data.onChange) {
                      data.onChange({
                        ...data,
                        createIfNotExists: e.target.checked,
                      });
                    }
                  }}
                  className="w-4 h-4 text-brand-gold bg-brand-smoke border-brand-gold/20 rounded focus:ring-brand-gold"
                />
                <Label htmlFor="createIfNotExists" className="text-brand-ivory text-xs cursor-pointer">
                  Criar cliente se não existir
                </Label>
              </div>

              {data.createIfNotExists && (
                <div>
                  <Label className="text-brand-ivory text-xs mb-1">
                    Nome do Cliente (opcional)
                  </Label>
                  <Input
                    value={data.clientName || ''}
                    onChange={(e) => {
                      if (data.onChange) {
                        data.onChange({
                          ...data,
                          clientName: e.target.value,
                        });
                      }
                    }}
                    placeholder="Campo do contexto ou texto fixo"
                    className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory text-sm"
                  />
                  <p className="text-xs text-brand-ivory/40 mt-1">
                    Use {'{campo}'} para contexto ou deixe fixo
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-green-400">Cliente Existe</span>
              <div className="w-3 h-3 rounded-full bg-green-400/20 border border-green-400" />
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-red-400">Cliente Não Existe</span>
              <div className="w-3 h-3 rounded-full bg-red-400/20 border border-red-400" />
            </div>
          </div>
        </div>
      </Card>

      {/* Handle para saída "existe" */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="exists"
        style={{ left: '30%' }}
        className="w-3 h-3 !bg-green-400 border-2 border-brand-ink"
      />

      {/* Handle para saída "não existe" */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="notExists"
        style={{ left: '70%' }}
        className="w-3 h-3 !bg-red-400 border-2 border-brand-ink"
      />
    </div>
  );
}

export default memo(CheckClientNode);
